import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import * as LucideIcons from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';

import { useAppContext } from '../AppContext';
import { db } from '../firebase-config';
import LoadingSpinner from './LoadingSpinner';
import ConnectionStrength from './ConnectionStrength';
import HubControls from './HubControls';
import QuickActionsMenu from './QuickActionsMenu';
import ConnectionCardSkeleton from './ConnectionCardSkeleton';
import BulkActionsFooter from './BulkActionsFooter';
import QuestTracker from './QuestTracker';
import MyQRCodeModal from './MyQRCodeModal';
import ShowdownModal from './ShowdownModal';

import { useTranslation } from 'react-i18next';

try {
    TimeAgo.addLocale(en);
    TimeAgo.addDefaultLocale(en);
} catch (e) {
    // Already registered
}

const timeAgo = new TimeAgo('en');

// Haptic feedback utility
const triggerHaptic = () => {
    if (window.navigator && window.navigator.vibrate) {
        window.navigator.vibrate(50); // A short, subtle vibration
    }
};

const UserCard = ({ user, connection, isBlocked, isLoading, loadingAction, handleAction, handleUserSelect, strengthScore, onSetTier, onGiftEchoes, onShareProfile, onChallenge, activity, isSelectMode, isSelected, onToggleSelect }) => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const cardVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
    const lastActiveTimestamp = user.lastActive ? timeAgo.format(user.lastActive.toDate(), 'round') : null;
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    const hasRecentActivity = activity?.lastDreamWeaveUpdate && new Date(activity.lastDreamWeaveUpdate) > threeDaysAgo;

    const handleClick = () => {
        triggerHaptic();
        if (isSelectMode) {
            onToggleSelect(user.id);
        } else {
            handleUserSelect(user.id);
        }
    }

    const handleMenuClick = (e) => {
        e.stopPropagation();
        triggerHaptic();
        setIsMenuOpen(prev => !prev);
    }

    return (
        <motion.div layout variants={cardVariants} onClick={handleClick} className={`relative p-4 rounded-lg flex flex-col justify-between border transition-colors duration-200 ${isSelectMode ? 'cursor-pointer' : ''} ${isSelected ? 'bg-cyan-900/50 border-cyan-600' : 'bg-gray-800/50 border-gray-700/50'}`}>
            {isSelectMode && <div className="absolute top-2 right-2 z-10 pointer-events-none"><div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${isSelected ? 'bg-cyan-500 border-cyan-400' : 'bg-gray-900/50 border-gray-600'}`}>{isSelected && <LucideIcons.Check size={16} className="text-white" />}</div></div>}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 mb-4 flex-grow min-w-0">
                    <ConnectionStrength score={strengthScore}><img src={user.photoURL || `https://placehold.co/40x40/AEC6CF/FFFFFF?text=${user.displayName.charAt(0)}`} alt={user.displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" /></ConnectionStrength>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5"><p className="font-semibold text-white truncate">{user.displayName}</p>{hasRecentActivity && <div title={t('manageConnections.recentActivity')}><LucideIcons.Sparkles size={16} className="text-yellow-400" /></div>}</div>
                        {user.mutualConnections ? <p className="text-xs text-cyan-400">{t('manageConnections.mutualConnections', { count: user.mutualConnections })}</p> : <p className="text-xs text-gray-400">{t('manageConnections.reputation', { reputation: user.reputation || 0 })}</p>}
                        {lastActiveTimestamp && <p className="text-xs text-gray-500 italic">{lastActiveTimestamp}</p>}
                        {user.primaryNexus && <div className="mt-1.5 inline-flex items-center gap-1.5 bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded-full text-xs"><LucideIcons.Shield size={12} /><span>{user.primaryNexus.name}</span></div>}
                    </div>
                </div>
                {!isSelectMode && connection && <div className="relative flex-shrink-0"><button onClick={handleMenuClick} className="p-1 rounded-full text-gray-400 hover:bg-gray-700/80 hover:text-white"><LucideIcons.MoreVertical size={20} /></button><QuickActionsMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} currentTier={connection?.tier || 'Following'} onSetTier={(tier) => onSetTier(user.id, tier)} onRemoveConnection={() => handleAction(user.id, 'disconnect')} onGiftEchoes={() => onGiftEchoes(user.id, user.displayName)} onShareProfile={() => onShareProfile(user.id)} onChallenge={() => onChallenge(user.id)} /></div>}
            </div>
            <div className="flex items-center gap-2 justify-end">{isLoading ? <LoadingSpinner message={t('manageConnections.loadingAction', { action: loadingAction.action })} /> : <>{isBlocked ? <button onClick={(e) => { e.stopPropagation(); handleAction(user.id, 'unblock'); }} className="small-action-button bg-gray-500 hover:bg-gray-400">{t('manageConnections.unblock')}</button> : <> {connection ? <button onClick={(e) => { e.stopPropagation(); handleAction(user.id, 'disconnect'); }} className="small-action-button bg-red-600 hover:bg-red-500">{t('manageConnections.disconnect')}</button> : <button onClick={(e) => { e.stopPropagation(); handleAction(user.id, 'connect'); }} className="small-action-button bg-blue-600 hover:bg-blue-500">{t('manageConnections.connect')}</button>} <button onClick={(e) => { e.stopPropagation(); handleAction(user.id, 'block'); }} className="small-action-button bg-gray-700 hover:bg-gray-600" title={t('manageConnections.blockUser' )}><LucideIcons.ShieldOff size={16} /></button></>}</>}</div>
        </motion.div>
    );
};

function ManageConnections() {
            const { userProfiles, userConnections, currentUserProfile, handleUserSelect, appFunctions, showConfirmation, setMessage, userId, appId, setUserConnections, onlineStatus } = useAppContext();
    const [activeTab, setActiveTab] = useState('connected');
    const [loadingAction, setLoadingAction] = useState(null);
    const [strengthScores, setStrengthScores] = useState({});
    const [sortOption, setSortOption] = useState('strength');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTier, setFilterTier] = useState('All Tiers');
    const [suggestedUsers, setSuggestedUsers] = useState([]);
    const [diverseSuggestions, setDiverseSuggestions] = useState([]);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [connectionsActivity, setConnectionsActivity] = useState({});
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [showMyCodeModal, setShowMyCodeModal] = useState(false);
    const [showdownTargetId, setShowdownTargetId] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [lastVisibleUser, setLastVisibleUser] = useState(null);
    const [hasMoreUsers, setHasMoreUsers] = useState(true);
    const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        if (!userId || !db) return;
        const suggestionsRef = collection(db, `artifacts/${appId}/users/${userId}/connection_suggestions`);
        const q = query(suggestionsRef, orderBy('suggestedAt', 'desc'), limit(20));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newSuggestions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSuggestedUsers(newSuggestions);
        });

        return () => unsubscribe();
    }, [userId, db, appId]);

    const fetchMoreUsers = useCallback(async () => {
        if (isLoadingAllUsers || !hasMoreUsers) return;
        setIsLoadingAllUsers(true);
        try {
            const getAllUsers = httpsCallable(appFunctions, 'getAllUsers');
            const result = await getAllUsers({ lastVisible: lastVisibleUser });
            setAllUsers(prev => [...prev, ...result.data.users]);
            setLastVisibleUser(result.data.lastVisible);
            setHasMoreUsers(result.data.users.length > 0);
        } catch (error) {
            setMessage(t('manageConnections.loadUsersError'));
        } finally {
            setIsLoadingAllUsers(false);
        }
    }, [appFunctions, hasMoreUsers, isLoadingAllUsers, lastVisibleUser, setMessage, t]);

    useEffect(() => {
        if (activeTab === 'all' && allUsers.length === 0) {
            fetchMoreUsers();
        }
    }, [activeTab, allUsers.length, fetchMoreUsers]);
    
    useEffect(() => {
        const fetchStrengths = async () => {
            try {
                const calculateConnectionStrength = httpsCallable(appFunctions, 'calculateConnectionStrength');
                const result = await calculateConnectionStrength({ appId });
                setStrengthScores(result.data);
            } catch (error) { console.error(t('manageConnections.fetchStrengthsError'), error); }
        };
        if (userConnections.length > 0) fetchStrengths();
    }, [userConnections, appFunctions, appId, t]);

    const connectionsMap = useMemo(() => new Map(userConnections.map(c => [c.followingId, c])), [userConnections]);
    const blockedUserIds = useMemo(() => new Set(currentUserProfile?.blockedUsers || []), [currentUserProfile]);

    const processedUsers = useMemo(() => {
        let users;
        switch (activeTab) {
            case 'connected': users = userProfiles.filter(p => connectionsMap.has(p.id)); break;
            case 'suggestions': return [];
            case 'online': users = userProfiles.filter(p => onlineStatus[p.id]?.isOnline && p.id !== userId); break;
            case 'blocked': users = userProfiles.filter(p => blockedUserIds.has(p.id)); break;
            case 'all': users = allUsers.filter(p => p.id !== userId); break;
            default: users = [];
        }
        if (activeTab === 'connected' && filterTier !== 'All Tiers') {
            users = users.filter(user => (connectionsMap.get(user.id)?.tier || 'Following') === filterTier);
        }
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        if (lowercasedSearchTerm) {
            users = users.filter(user => user.displayName.toLowerCase().includes(lowercasedSearchTerm));
        }
        return users.sort((a, b) => {
            switch (sortOption) {
                case 'strength': return (strengthScores[b.id] || 0) - (strengthScores[a.id] || 0);
                case 'alphabetical': return a.displayName.localeCompare(b.displayName);
                case 'lastActive': return (b.lastActive?.toDate() || 0) - (a.lastActive?.toDate() || 0);
                default: return 0;
            }
        });
    }, [activeTab, userProfiles, connectionsMap, blockedUserIds, userId, sortOption, strengthScores, searchTerm, filterTier, allUsers]);

    useEffect(() => {
        const fetchActivity = async () => {
            if (processedUsers.length === 0) return;
            const ids = processedUsers.map(u => u.id);
            try {
                const getConnectionsActivity = httpsCallable(appFunctions, 'getConnectionsActivity');
                const result = await getConnectionsActivity({ userIds: ids, appId });
                setConnectionsActivity(result.data);
            } catch (error) { console.error("Error fetching activity:", error); }
        };
        if(activeTab !== 'suggestions') fetchActivity();
    }, [processedUsers, appFunctions, appId, activeTab]);

    const handleSetTier = useCallback(async (connectionUserId, tier) => {
        setLoadingAction({ action: 'tier', userId: connectionUserId });
        try {
            const setConnectionTier = httpsCallable(appFunctions, 'setConnectionTier');
            await setConnectionTier({ connectionUserId, tier, appId });
            setMessage(t('manageConnections.setTierSuccess', { tier }));
        } catch (error) { setMessage(t('manageConnections.generalError', { message: error.message })); }
        finally { setLoadingAction(null); }
    }, [appFunctions, appId, setMessage, t]);

    const handleGiftEchoes = useCallback((recipientId, recipientName) => {
        showConfirmation({
            message: t('manageConnections.giftConfirmation', { name: recipientName }),
            onConfirm: async () => {
                setLoadingAction({ action: 'gift', userId: recipientId });
                try {
                    const giftEchoesFn = httpsCallable(appFunctions, 'giftEchoes');
                    await giftEchoesFn({ recipientId, appId });
                    setMessage(t('manageConnections.giftSuccess', { name: recipientName }));
                } catch (error) { setMessage(t('manageConnections.giftFailed', { message: error.message })); }
                finally { setLoadingAction(null); }
            }
        });
    }, [appFunctions, appId, showConfirmation, setMessage, t]);

    const handleShareProfile = useCallback((profileId) => {
        const url = `${window.location.origin}${window.location.pathname}?page=viewingProfile&userId=${profileId}`;
        navigator.clipboard.writeText(url).then(() => {
            setMessage(t('manageConnections.copySuccess'));
        }, () => {
            setMessage(t('manageConnections.copyError'));
        });
    }, [setMessage, t]);

    const handleAction = (targetUserId, action) => {
        triggerHaptic();
        const targetProfile = userProfiles.find(p => p.id === targetUserId) || suggestedUsers.find(p => p.id === targetUserId) || diverseSuggestions.find(p => p.id === targetUserId);
        if (!targetProfile) return;
        let message = '';
        let confirmAction;
        switch (action) {
            case 'connect': message = t('manageConnections.connectConfirmation', { name: targetProfile.displayName }); confirmAction = () => followUser(targetUserId); break;
            case 'disconnect': message = t('manageConnections.disconnectConfirmation', { name: targetProfile.displayName }); confirmAction = () => unfollowUser(targetUserId); break;
            case 'block': message = t('manageConnections.blockConfirmation', { name: targetProfile.displayName }); confirmAction = () => blockUser(targetUserId); break;
            case 'unblock': message = t('manageConnections.unblockConfirmation', { name: targetProfile.displayName }); confirmAction = () => unblockUser(targetUserId); break;
            default: return;
        }
        showConfirmation({ message, onConfirm: confirmAction });
    };

    const followUser = async (targetUserId) => {
        setLoadingAction({ action: 'connect', userId: targetUserId });
        try {
            const followUserFn = httpsCallable(appFunctions, 'followUser');
            await followUserFn({ userIdToFollow: targetUserId });
            setMessage(t('manageConnections.connectSuccess'));
            setSuggestedUsers(prev => prev.filter(u => u.id !== targetUserId));
            setDiverseSuggestions(prev => prev.filter(u => u.id !== targetUserId));
            setUserConnections(prev => [...prev, { followingId: targetUserId }]);
        } catch (error) { setMessage(t('manageConnections.generalError', { message: error.message })); }
        finally { setLoadingAction(null); }
    };

    const unfollowUser = async (targetUserId) => {
        setLoadingAction({ action: 'disconnect', userId: targetUserId });
        try {
            const unfollowUserFn = httpsCallable(appFunctions, 'unfollowUser');
            await unfollowUserFn({ userIdToUnfollow: targetUserId });
            setMessage(t('manageConnections.disconnectSuccess'));
            setUserConnections(prev => prev.filter(c => c.followingId !== targetUserId));
        } catch (error) { setMessage(t('manageConnections.generalError', { message: error.message })); }
        finally { setLoadingAction(null); }
    };

    const blockUser = async (targetUserId) => {
        setLoadingAction({ action: 'block', userId: targetUserId });
        try {
            const blockUserFn = httpsCallable(appFunctions, 'blockUser');
            await blockUserFn({ userIdToBlock: targetUserId });
            setMessage(t('manageConnections.blockSuccess'));
        } catch (error) { setMessage(t('manageConnections.generalError', { message: error.message })); }
        finally { setLoadingAction(null); }
    };

    const unblockUser = async (targetUserId) => {
        setLoadingAction({ action: 'unblock', userId: targetUserId });
        try {
            const unblockUserFn = httpsCallable(appFunctions, 'unblockUser');
            await unblockUserFn({ userIdToUnblock: targetUserId });
            setMessage(t('manageConnections.unblockSuccess'));
        } catch (error) { setMessage(t('manageConnections.generalError', { message: error.message })); }
        finally { setLoadingAction(null); }
    };

    const onToggleSelectMode = () => {
        triggerHaptic();
        setIsSelectMode(prev => !prev);
        setSelectedUsers(new Set());
    }

    const onToggleSelect = (selectedUserId) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(selectedUserId)) newSet.delete(selectedUserId); else newSet.add(selectedUserId);
            return newSet;
        });
    }

    const handleBulkUpdateTier = useCallback(async (tier) => {
        const userIds = Array.from(selectedUsers);
        setLoadingAction({ action: 'bulk-tier', userIds });
        try {
            const bulkUpdateTiersFn = httpsCallable(appFunctions, 'bulkUpdateTiers');
            const result = await bulkUpdateTiersFn({ connectionUserIds: userIds, tier, appId });
            setMessage(result.data.message);
            onToggleSelectMode();
        } catch (error) { setMessage(t('manageConnections.bulkUpdateFailed', { message: error.message })); }
        finally { setLoadingAction(null); }
    }, [selectedUsers, appFunctions, appId, setMessage, t]);
    
    const tabCounts = useMemo(() => ({
        connected: connectionsMap.size,
        suggestions: suggestedUsers.length + diverseSuggestions.length,
        online: userProfiles.filter(p => onlineStatus[p.id]?.isOnline && p.id !== userId).length,
        blocked: blockedUserIds.size,
        all: userProfiles.filter(p => p.id !== userId).length,
    }), [connectionsMap, suggestedUsers, diverseSuggestions, userProfiles, userId, blockedUserIds, onlineStatus]);

    const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } };

    const renderContent = () => {
        if (activeTab === 'suggestions') {
            if (isLoadingSuggestions) return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"><ConnectionCardSkeleton /><ConnectionCardSkeleton /><ConnectionCardSkeleton /></div>;
            return (
                <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {suggestedUsers.length > 0 ? (
                        suggestedUsers.map(user => <UserCard key={user.id} user={user} handleAction={handleAction} handleUserSelect={handleUserSelect} />)
                    ) : (
                        <p className="text-sm text-gray-500 italic col-span-full">{t('manageConnections.suggestions.noNewSuggestions')}</p>
                    )}
                </motion.div>
            );
        }
        if (processedUsers.length > 0) {
            return (
                <>
                    {processedUsers.map(user => <UserCard key={user.id} user={user} connection={connectionsMap.get(user.id)} isBlocked={blockedUserIds.has(user.id)} isLoading={loadingAction?.userId === user.id} loadingAction={loadingAction} handleAction={handleAction} handleUserSelect={handleUserSelect} strengthScore={strengthScores[user.id] || 0} onSetTier={handleSetTier} onGiftEchoes={handleGiftEchoes} onShareProfile={handleShareProfile} onChallenge={setShowdownTargetId} activity={connectionsActivity[user.id]} isSelectMode={isSelectMode} isSelected={selectedUsers.has(user.id)} onToggleSelect={onToggleSelect} />)}
                    {activeTab === 'all' && hasMoreUsers && (
                        <div className="col-span-full flex justify-center">
                            <button onClick={fetchMoreUsers} disabled={isLoadingAllUsers} className="small-action-button bg-gray-700 hover:bg-gray-600">
                                {isLoadingAllUsers ? <LoadingSpinner /> : t('manageConnections.loadMore')}
                            </button>
                        </div>
                    )}
                </>
            );
        }
        return <p className="text-center text-gray-400 italic py-8 col-span-full">{t('manageConnections.noUsersFound')}</p>;
    }

    return (
        <div className="animate-fadeIn">
            {showdownTargetId && <ShowdownModal onClose={() => setShowdownTargetId(null)} rivalId={showdownTargetId} challengeMode={true} />}
            {showMyCodeModal && <MyQRCodeModal onClose={() => setShowMyCodeModal(false)} />}
            <QuestTracker />
            <div className="flex justify-center border-b border-gray-700 mb-4">
                <button onClick={() => setActiveTab('connected')} className={`profile-tab-button ${activeTab === 'connected' ? 'active' : ''}`}>{t('manageConnections.tabs.connected')} <span className="text-gray-400 text-xs ml-1.5">{tabCounts.connected}</span></button>
                <button onClick={() => setActiveTab('suggestions')} className={`profile-tab-button ${activeTab === 'suggestions' ? 'active' : ''}`}>{t('manageConnections.tabs.suggestions')} <span className="text-gray-400 text-xs ml-1.5">{tabCounts.suggestions}</span></button>
                <button onClick={() => setActiveTab('online')} className={`profile-tab-button ${activeTab === 'online' ? 'active' : ''}`}>{t('manageConnections.tabs.online')} <span className="text-gray-400 text-xs ml-1.5">{tabCounts.online}</span></button>
                <button onClick={() => setActiveTab('blocked')} className={`profile-tab-button ${activeTab === 'blocked' ? 'active' : ''}`}>{t('manageConnections.tabs.blocked')} <span className="text-gray-400 text-xs ml-1.5">{tabCounts.blocked}</span></button>
                <button onClick={() => setActiveTab('all')} className={`profile-tab-button ${activeTab === 'all' ? 'active' : ''}`}>{t('manageConnections.tabs.all')} <span className="text-gray-400 text-xs ml-1.5">{tabCounts.all}</span></button>
            </div>
            {activeTab !== 'suggestions' && <HubControls isSelectMode={isSelectMode} onToggleSelectMode={onToggleSelectMode} onShowMyCode={() => { triggerHaptic(); setShowMyCodeModal(true); }} {...{ sortOption, setSortOption, searchTerm, setSearchTerm, filterTier, setFilterTier }} />}
            <div className="max-h-[60vh] overflow-y-auto custom-scrollbar pr-2 pb-24">
                {activeTab !== 'suggestions' ? <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{renderContent()}</motion.div> : renderContent()}
            </div>
            <AnimatePresence>
                {isSelectMode && <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}><BulkActionsFooter selectedCount={selectedUsers.size} onClear={() => { triggerHaptic(); setSelectedUsers(new Set()); }} onBulkUpdateTier={(tier) => { triggerHaptic(); handleBulkUpdateTier(tier); }} /></motion.div>}
            </AnimatePresence>
        </div>
    );
}

export default ManageConnections;